import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  DataTable,
  Tabs,
  ToggleSwitch,
  type DataTableColumn,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import {
  ICaptureDevice,
  IWatchFolderMount,
} from '@dataTypes/dicom-capture.type'
import { useAppSelector } from '@store/hooks'
import { useClinicOperatories } from '@hooks/useClinicOperatories'
import { useTheme } from '@context/ThemeContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import { useSettingsTabSkeleton } from '@pages/Settings/useSettingsTabSkeleton'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import useDicomCapture from './useDicomCapture'
import AddDeviceModal from './AddDeviceModal.component'
import AddWatchFolderModal from './AddWatchFolderModal.component'
import {
  formatLastSeen,
  isRecentlySeen,
  operatoryLabel,
  patientRoutingLabel,
} from './dicomCapture.data'
import './DicomCaptureStyle.css'

type TabKey = 'devices' | 'watch-folders'

const TAB_ITEMS: { id: TabKey; label: string }[] = [
  { id: 'devices', label: 'Devices' },
  { id: 'watch-folders', label: 'Watch folders' },
]

type DeviceRow = {
  id: string
  online: boolean
  status: string
  statusSort: string
  label: string
  aeTitle: string
  allowedIps: string
  operatory: string
  routing: string
  enabled: boolean
}

type MountRow = {
  id: string
  label: string
  rootPath: string
  operatory: string
  recursive: string
  routing: string
  enabled: boolean
}

const readDeviceSortValue = (row: DeviceRow, columnId: string): string =>
  columnId === 'status'
    ? row.statusSort
    : String(row[columnId as keyof DeviceRow] ?? '').toLowerCase()

function DicomCapture(): React.ReactElement {
  const hooks = useDicomCapture()
  useClinicOperatories()
  const operatories = useAppSelector((s) => s.operatory.data)
  const { setHeaderButtons } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const disabled = hooks.loading || hooks.saving || !hooks.clinicId

  const [activeTab, setActiveTab] = useState<TabKey>('devices')
  const isTabSkeleton = useSettingsTabSkeleton(activeTab)

  const [deviceModalOpen, setDeviceModalOpen] = useState(false)
  const [editingDevice, setEditingDevice] = useState<ICaptureDevice | null>(
    null,
  )
  const [deviceToDelete, setDeviceToDelete] = useState<ICaptureDevice | null>(
    null,
  )

  const [watchModalOpen, setWatchModalOpen] = useState(false)
  const [editingMount, setEditingMount] = useState<IWatchFolderMount | null>(
    null,
  )
  const [mountToDelete, setMountToDelete] = useState<IWatchFolderMount | null>(
    null,
  )
  const [testingId, setTestingId] = useState<string | null>(null)

  const [deviceSort, toggleDeviceSort] = useTableSort()
  const [mountSort, toggleMountSort] = useTableSort()

  const openAddDevice = useCallback((): void => {
    setEditingDevice(null)
    setDeviceModalOpen(true)
  }, [])

  const openEditDevice = useCallback((device: ICaptureDevice): void => {
    setEditingDevice(device)
    setDeviceModalOpen(true)
  }, [])

  const openAddMount = useCallback((): void => {
    setEditingMount(null)
    setWatchModalOpen(true)
  }, [])

  const openEditMount = useCallback((mount: IWatchFolderMount): void => {
    setEditingMount(mount)
    setWatchModalOpen(true)
  }, [])

  const { testDevice } = hooks
  const handleTestDevice = useCallback(
    async (id: string): Promise<void> => {
      setTestingId(id)
      try {
        await testDevice(id)
      } finally {
        setTestingId(null)
      }
    },
    [testDevice],
  )

  const handleConfirmDeleteDevice = useCallback(async (): Promise<void> => {
    if (!deviceToDelete) return
    try {
      await hooks.deleteDevice(deviceToDelete.capture_device_id)
      setDeviceToDelete(null)
    } catch {
      // Parent surfaces the error toast; keep the modal open so the user can retry.
    }
  }, [deviceToDelete, hooks])

  const handleConfirmDeleteMount = useCallback(async (): Promise<void> => {
    if (!mountToDelete) return
    try {
      await hooks.deleteWatchMount(mountToDelete.watch_folder_mount_id)
      setMountToDelete(null)
    } catch {
      // Parent surfaces the error toast; keep the modal open so the user can retry.
    }
  }, [mountToDelete, hooks])

  const deviceById = useMemo(
    () => new Map(hooks.devices.map((d) => [d.capture_device_id, d])),
    [hooks.devices],
  )
  const mountById = useMemo(
    () => new Map(hooks.watchMounts.map((m) => [m.watch_folder_mount_id, m])),
    [hooks.watchMounts],
  )

  const deviceRows: DeviceRow[] = useMemo(
    () =>
      hooks.devices.map((d) => ({
        id: d.capture_device_id,
        online: isRecentlySeen(d.last_seen_at),
        status: formatLastSeen(d.last_seen_at),
        statusSort: d.last_seen_at ?? '',
        label: d.label,
        aeTitle: d.calling_ae_title,
        allowedIps: d.allowed_remote_ips || 'Any',
        operatory: operatoryLabel(d.operatory_id, operatories),
        routing: patientRoutingLabel(d.default_patient_routing),
        enabled: d.enabled,
      })),
    [hooks.devices, operatories],
  )

  const mountRows: MountRow[] = useMemo(
    () =>
      hooks.watchMounts.map((m) => ({
        id: m.watch_folder_mount_id,
        label: m.label,
        rootPath: m.root_path,
        operatory: operatoryLabel(m.operatory_id, operatories),
        recursive: m.recursive ? 'Yes' : 'No',
        routing: patientRoutingLabel(m.default_patient_routing),
        enabled: m.enabled,
      })),
    [hooks.watchMounts, operatories],
  )

  const sortedDeviceRows = useMemo(
    () => sortRows(deviceRows, deviceSort, readDeviceSortValue),
    [deviceRows, deviceSort],
  )
  const sortedMountRows = useMemo(
    () => sortRows(mountRows, mountSort),
    [mountRows, mountSort],
  )
  const devicePage = usePagedRows(sortedDeviceRows, { alwaysShow: true })
  const mountPage = usePagedRows(sortedMountRows, { alwaysShow: true })

  const isDevicesTab = activeTab === 'devices'
  const activeListEmpty = isDevicesTab
    ? hooks.devices.length === 0
    : hooks.watchMounts.length === 0

  useEffect(() => {
    setHeaderButtons(
      activeListEmpty ? null : (
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          disabled={disabled}
          onClick={isDevicesTab ? openAddDevice : openAddMount}
        >
          {isDevicesTab ? 'Add device' : 'Add watch folder'}
        </Button>
      ),
    )
    return () => setHeaderButtons(null)
  }, [
    activeListEmpty,
    disabled,
    isDevicesTab,
    openAddDevice,
    openAddMount,
    setHeaderButtons,
  ])

  const { toggleDevice, toggleWatchMount } = hooks

  const deviceColumns: DataTableColumn<DeviceRow>[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        width: 160,
        renderCell: (row) => (
          <Badge
            mode={badgeMode}
            color={row.online ? 'vivid-green' : 'gray'}
            label={row.status}
          />
        ),
      },
      { id: 'label', label: 'Label', sortable: true },
      { id: 'aeTitle', label: 'Calling AE', sortable: true, width: 130 },
      { id: 'allowedIps', label: 'Allowed IPs', sortable: true },
      { id: 'operatory', label: 'Operatory', sortable: true, width: 150 },
      { id: 'routing', label: 'Routing', sortable: true, width: 210 },
      {
        id: 'enabled',
        label: 'Enabled',
        width: 96,
        renderCell: (row) => {
          const device = deviceById.get(row.id)
          return (
            <ToggleSwitch
              aria-label={`${row.label} enabled`}
              checked={row.enabled}
              disabled={disabled || !device}
              onChange={() => device && void toggleDevice(device)}
            />
          )
        },
      },
    ],
    [badgeMode, deviceById, disabled, toggleDevice],
  )

  const mountColumns: DataTableColumn<MountRow>[] = useMemo(
    () => [
      { id: 'label', label: 'Label', sortable: true },
      { id: 'rootPath', label: 'Folder path', sortable: true },
      { id: 'operatory', label: 'Operatory', sortable: true, width: 150 },
      { id: 'recursive', label: 'Recursive', sortable: true, width: 110 },
      { id: 'routing', label: 'Routing', sortable: true, width: 210 },
      {
        id: 'enabled',
        label: 'Enabled',
        width: 96,
        renderCell: (row) => {
          const mount = mountById.get(row.id)
          return (
            <ToggleSwitch
              aria-label={`${row.label} enabled`}
              checked={row.enabled}
              disabled={disabled || !mount}
              onChange={() => mount && void toggleWatchMount(mount)}
            />
          )
        },
      },
    ],
    [disabled, mountById, toggleWatchMount],
  )

  const tableState = (rowCount: number): 'loading' | 'empty' | 'default' =>
    !hooks.dataReady ? 'loading' : rowCount === 0 ? 'empty' : 'default'

  const scp = hooks.scpInfo

  return (
    <div className="dicom-capture-settings" id="dicom-capture-settings">
      <section className="dc-scp-panel" aria-labelledby="dc-scp-title">
        <h2 className="dc-scp-title" id="dc-scp-title">
          <i className="mgc_information_fill" aria-hidden="true" />
          Tell your device to connect to…
        </h2>
        {scp && scp.enabled ? (
          <dl className="dc-scp-grid">
            <div className="dc-scp-item">
              <dt className="dc-scp-label">AE title</dt>
              <dd className="dc-scp-value">{scp.aeTitle ?? '—'}</dd>
            </div>
            <div className="dc-scp-item">
              <dt className="dc-scp-label">Host</dt>
              <dd className="dc-scp-value">{scp.host ?? '—'}</dd>
            </div>
            <div className="dc-scp-item">
              <dt className="dc-scp-label">Port</dt>
              <dd className="dc-scp-value">{scp.port ?? '—'}</dd>
            </div>
            <div className="dc-scp-item">
              <dt className="dc-scp-label">Listener</dt>
              <dd className="dc-scp-value">
                <Badge
                  mode={badgeMode}
                  color={scp.listening ? 'vivid-green' : 'red'}
                  label={scp.listening ? 'Online' : 'Offline'}
                />
              </dd>
            </div>
          </dl>
        ) : (
          <p className="dc-scp-disabled">
            The DIMSE C-STORE listener is disabled or unreachable for this site.
            Watch-folder ingestion can still be configured below.
          </p>
        )}
      </section>

      <Tabs
        className="dc-tabs"
        items={TAB_ITEMS}
        value={activeTab}
        onChange={(id) => setActiveTab(id as TabKey)}
      />

      {isTabSkeleton ? (
        <SettingsSkeleton
          variant="table"
          pageKey={
            isDevicesTab
              ? 'clinical-dicom-capture'
              : 'clinical-dicom-capture-watch-folders'
          }
        />
      ) : isDevicesTab ? (
        <DataTable<DeviceRow>
          className="dc-table dc-table--devices"
          columns={deviceColumns}
          rows={devicePage.pageRows}
          state={tableState(deviceRows.length)}
          emptyState={{
            title: 'No capture devices found',
            description: "You don't have any capture devices on record yet.",
            actionLabel: 'Add device',
            onAction: openAddDevice,
          }}
          sortColumnId={deviceSort.columnId}
          sortDirection={deviceSort.columnId ? deviceSort.direction : undefined}
          onSort={toggleDeviceSort}
          actionsLabel="Action/s"
          renderActions={(row) => {
            const device = deviceById.get(row.id)
            const isTesting = testingId === row.id
            return (
              <div className="dc-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className="dc-test-btn"
                  disabled={isTesting || disabled}
                  onClick={() => {
                    void handleTestDevice(row.id)
                  }}
                >
                  {isTesting ? 'Testing…' : 'Test connection'}
                </Button>
                <SettingsRowActions
                  disabled={!device}
                  onEdit={() => device && openEditDevice(device)}
                  onDelete={() => device && setDeviceToDelete(device)}
                />
              </div>
            )
          }}
          pagination={devicePage.pagination}
        />
      ) : (
        <DataTable<MountRow>
          className="dc-table"
          columns={mountColumns}
          rows={mountPage.pageRows}
          state={tableState(mountRows.length)}
          emptyState={{
            title: 'No watch folders found',
            description: "You don't have any watch folders on record yet.",
            actionLabel: 'Add watch folder',
            onAction: openAddMount,
          }}
          sortColumnId={mountSort.columnId}
          sortDirection={mountSort.columnId ? mountSort.direction : undefined}
          onSort={toggleMountSort}
          actionsLabel="Action/s"
          renderActions={(row) => {
            const mount = mountById.get(row.id)
            return (
              <SettingsRowActions
                disabled={!mount}
                onEdit={() => mount && openEditMount(mount)}
                onDelete={() => mount && setMountToDelete(mount)}
              />
            )
          }}
          pagination={mountPage.pagination}
        />
      )}

      <AddDeviceModal
        isOpen={deviceModalOpen}
        clinicId={hooks.clinicId}
        operatories={operatories}
        mode={editingDevice ? 'edit' : 'add'}
        device={editingDevice}
        onRequestClose={() => setDeviceModalOpen(false)}
        onSubmit={hooks.saveDevice}
        isSubmitting={hooks.saving}
      />

      <AddWatchFolderModal
        isOpen={watchModalOpen}
        clinicId={hooks.clinicId}
        operatories={operatories}
        mode={editingMount ? 'edit' : 'add'}
        mount={editingMount}
        onRequestClose={() => setWatchModalOpen(false)}
        onSubmit={hooks.saveWatchMount}
        isSubmitting={hooks.saving}
      />

      <DeleteConfirmationModal
        show={!!deviceToDelete}
        onHide={() => setDeviceToDelete(null)}
        onConfirm={() => {
          void handleConfirmDeleteDevice()
        }}
        entityName={deviceToDelete?.label ?? ''}
        title="Delete capture device?"
        bodyText={`Are you sure you want to delete capture device "${deviceToDelete?.label ?? ''}"? This action can't be undone.`}
        confirmDisabled={hooks.saving}
      />

      <DeleteConfirmationModal
        show={!!mountToDelete}
        onHide={() => setMountToDelete(null)}
        onConfirm={() => {
          void handleConfirmDeleteMount()
        }}
        entityName={mountToDelete?.label ?? ''}
        title="Delete watch folder?"
        bodyText={`Are you sure you want to delete watch folder "${mountToDelete?.label ?? ''}"? This action can't be undone.`}
        confirmDisabled={hooks.saving}
      />
    </div>
  )
}

export default DicomCapture
